import { useEffect, useState } from 'react'
import { baseMinorVersion } from '../release-history'

const STORAGE_KEY = 'alphaedge:lastSeenReleaseVersion'

// Auto-announce the newest release once per version (issue #236).
//
// Compares a localStorage marker to the *base minor* of the running
// version (baseMinorVersion() strips the "dN" dev-build suffix and any
// git-sha metadata) rather than the raw runtime string. That's a
// deliberate choice: dev-build bumps like 0.40.0d0 -> 0.40.0d3 land
// constantly between releases and share the same user-facing release
// notes, so they must not re-trigger the announce -- only an actual new
// minor (0.40.0 -> 0.41.0) should.
//
// NOTE: the app's auth token is deliberately kept in-memory only (never
// localStorage) -- that rule is specifically about auth. A "have I seen
// this release" marker carries no sensitive data, so localStorage is fine
// here and is what makes the flag survive a normal page reload.
export function useReleaseAnnounce(currentVersion: string): readonly [boolean, () => void] {
  const [showAnnounce, setShowAnnounce] = useState(false)

  useEffect(() => {
    if (!currentVersion) return
    const base = baseMinorVersion(currentVersion)
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY)
      if (seen !== base) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing from an external system (localStorage) is what effects are for
        setShowAnnounce(true)
        window.localStorage.setItem(STORAGE_KEY, base)
      }
    } catch {
      // localStorage unavailable (private mode, disabled storage, etc.) --
      // skip the announce rather than throwing.
    }
  }, [currentVersion])

  const dismiss = () => setShowAnnounce(false)

  return [showAnnounce, dismiss] as const
}
